// Domain-types and Zod-Schemas, shared between server and web.
//
// Conventions:
// - API-payloads use snake_case (mirrors DB columns and the spec in 02_ARCHITECTURE.md).
// - Dates without time are ISO-date strings ("YYYY-MM-DD"); timestamps are ISO-8601.
// - Schemas are split into "Create" (POST) and "Update" (PATCH) flavors.
//   Update-schemas are partial — clients only send fields they want to change.
import { z } from "zod";
// ---------- enums ----------
export const PROJECT_STATUSES = [
    "active",
    "paused",
    "done",
    "archived",
];
export const PHASE_STATUSES = ["planned", "active", "done"];
export const TASK_STATUSES = [
    "backlog",
    "in_progress",
    "review",
    "done",
    "blocked",
];
export const TASK_PRIORITIES = ["low", "medium", "high", "urgent"];
// Custom-status category axis (A1) — mirrors the DB `status_category` enum.
export const STATUS_CATEGORIES = [
    "backlog",
    "unstarted",
    "started",
    "review",
    "done",
    "cancelled",
];
// Sichtbarkeit eines Projekts (Schritt 1 Sharing).
//  - private: nur Owner + explizit freigegebene Member
//  - shared:  Owner + ausgewählte Member
//  - org:     alle internen User (Default, = bisheriges Verhalten)
export const PROJECT_VISIBILITIES = ["private", "shared", "org"];
// Rolle eines Users auf einem Projekt. "owner" ist implizit (owner_id),
// "viewer"/"editor" werden über project_members vergeben.
export const PROJECT_MEMBER_ROLES = ["viewer", "editor"];
// ---------- shared scalar shapes ----------
// ISO date (YYYY-MM-DD) — accept also null/undefined where nullable.
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD date");
// Hex color #RRGGBB or #RGB.
const hexColor = z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Expected hex color like #57A5CB");
// Decimal hours encoded as string (Drizzle numeric → string).
const decimalHours = z
    .union([z.string(), z.number()])
    .refine((v) => !isNaN(Number(v)) && Number(v) >= 0 && Number(v) < 10000, "Expected non-negative decimal hours < 10000");
// ---------- spaces (B4) ----------
export const SPACE_MEMBER_ROLES = ["viewer", "editor"];
export const SpaceCreateSchema = z.object({
    name: z.string().trim().min(1).max(100),
    icon: z.string().max(50).optional().nullable(),
    color: z
        .string()
        .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Expected hex color")
        .optional()
        .nullable(),
});
export const SpaceUpdateSchema = SpaceCreateSchema.partial().extend({
    sort_order: z.number().int().min(0).optional(),
});
export const ProjectCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(10_000).optional().nullable(),
    color: hexColor.optional().nullable(),
    status: z.enum(PROJECT_STATUSES).optional(),
    start_date: isoDate.optional().nullable(),
    target_date: isoDate.optional().nullable(),
    owner_id: z.number().int().positive().optional(),
    /** Schritt 1 Sharing: Sichtbarkeit. Default 'shared' (Space-Scope) wenn nicht
     *  gesetzt — organisationsweit nur auf explizite Angabe. */
    visibility: z.enum(PROJECT_VISIBILITIES).optional(),
    /** Sprint 3 Story 3: 0..n Abteilungen pro Projekt. Wenn leer → automatisch
     *  Gesamtorganisation. */
    department_ids: z.array(z.number().int().positive()).optional(),
    /** ADR create-space-folder: optional target Space. When absent the server
     *  falls back to the default 'general' Space (backward compatible). */
    space_id: z.number().int().positive().optional(),
    /** ADR create-space-folder: optional target Folder within the resolved
     *  Space. null clears it; absent defaults to null. */
    folder_id: z.number().int().positive().nullable().optional(),
});
export const ProjectUpdateSchema = ProjectCreateSchema.partial();
export const ProjectMemberCreateSchema = z.object({
    user_id: z.number().int().positive(),
    role: z.enum(PROJECT_MEMBER_ROLES).default("editor"),
});
export const ProjectMemberRoleUpdateSchema = z.object({
    role: z.enum(PROJECT_MEMBER_ROLES),
});
// ---------- task-level external sharing (Schritt 2) ----------
/** Einladung eines Externen zu EINER Aufgabe. */
export const TaskInviteCreateSchema = z.object({
    // zod 4: normalize (trim + lowercase) BEFORE the top-level z.email() check so
    // whitespace/case are stripped first, matching v3's chained-order behavior.
    email: z.string().trim().toLowerCase().pipe(z.email().max(200)),
    permission: z.enum(["read", "edit"]),
    note: z.string().max(500).optional(),
    expires_days: z.number().int().min(1).max(365).optional(),
});
/** Was der Gast an seiner Aufgabe ändern darf (enge Whitelist). */
export const GuestTaskPatchSchema = z
    .object({
    status: z.enum(TASK_STATUSES).optional(),
    description: z.string().max(10_000).nullable().optional(),
})
    .refine((v) => v.status !== undefined || v.description !== undefined, {
    message: "Nothing to update",
});
export const GuestCommentCreateSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
});
export const PhaseCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    color: hexColor.optional().nullable(),
    status: z.enum(PHASE_STATUSES).optional(),
    start_date: isoDate.optional().nullable(),
    end_date: isoDate.optional().nullable(),
});
export const PhaseUpdateSchema = PhaseCreateSchema.partial();
export const PhaseReorderSchema = z.object({
    sort_order: z.number().int().min(0),
});
export const TaskCreateSchema = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().max(50_000).optional().nullable(),
    priority: z.enum(TASK_PRIORITIES).optional().nullable(),
    /** Optional list of user-ids to assign. First entry becomes primary. */
    assignee_ids: z.array(z.number().int().positive()).max(50).optional(),
    /** Optional label ids to attach on creation. */
    label_ids: z.array(z.number().int().positive()).max(20).optional(),
    /** Optional parent task. The new task inherits the parent's phase. */
    parent_id: z.number().int().positive().optional().nullable(),
    start_date: isoDate.optional().nullable(),
    end_date: isoDate.optional().nullable(),
    deadline: isoDate.optional().nullable(),
    is_milestone: z.boolean().optional(),
    estimated_hours: decimalHours.optional().nullable(),
    actual_hours: decimalHours.optional().nullable(),
    /** D2: Per-project custom field values keyed by custom_field_defs.key. */
    custom_values: z.record(z.string(), z.unknown()).optional(),
    /** E3: Work-item type id. Validated server-side: must belong to the same project. */
    type_id: z.number().int().positive().optional(),
});
export const TaskUpdateSchema = TaskCreateSchema.partial().extend({
    /**
     * Move the task to a per-project custom status. Validated server-side to
     * belong to the task's project. `status_id` is the sole source of truth for
     * a task's status (legacy `tasks.status` was dropped in A3d).
     */
    status_id: z.number().int().positive().optional().nullable(),
    /**
     * Assign the task to a cycle. Validated server-side: cycle must exist and
     * belong to the same project as the task. `null` unassigns.
     */
    cycle_id: z.number().int().positive().optional().nullable(),
});
/**
 * K5a: Bulk task edit — `PATCH /api/tasks/batch`.
 * `ids` is hard-capped at 200 server-side (reject oversize 400, never truncate);
 * de-duplication happens in the handler. `patch` reuses the TaskUpdate field
 * shapes so the same validation/ownership rules apply per task.
 */
export const TaskBatchUpdateSchema = z.object({
    ids: z.array(z.number().int().positive()).min(1).max(200),
    patch: TaskUpdateSchema,
});
export const TaskReorderSchema = z.object({
    phase_id: z.number().int().positive().optional(),
    sort_order: z.number().int().min(0),
});
// Filters for GET /api/projects/:id/tasks?...
export const TaskFilterSchema = z.object({
    assignee_id: z.coerce.number().int().positive().optional(),
    status: z.enum(TASK_STATUSES).optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    phase_id: z.coerce.number().int().positive().optional(),
    is_milestone: z
        .union([z.literal("true"), z.literal("false"), z.boolean()])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === true || v === "true")),
    deadline_before: isoDate.optional(),
    deadline_after: isoDate.optional(),
});
export const ChecklistItemCreateSchema = z.object({
    title: z.string().trim().min(1).max(500),
});
export const ChecklistItemUpdateSchema = z
    .object({
    title: z.string().trim().min(1).max(500).optional(),
    is_done: z.boolean().optional(),
    sort_order: z.number().int().min(0).optional(),
})
    .refine((v) => v.title !== undefined || v.is_done !== undefined || v.sort_order !== undefined, {
    message: "Nothing to update",
});
/** Manual time entry: a positive duration in minutes (max 24h). */
export const TimeEntryCreateSchema = z.object({
    duration_minutes: z.number().int().min(1).max(24 * 60),
    note: z.string().trim().max(500).optional().nullable(),
    /** Optional ISO timestamp for when the work happened; defaults to now server-side. */
    started_at: z.iso.datetime().optional(),
});
export const TimeEntryUpdateSchema = z
    .object({
    duration_minutes: z.number().int().min(1).max(24 * 60).optional(),
    note: z.string().trim().max(500).optional().nullable(),
})
    .refine((v) => v.duration_minutes !== undefined || v.note !== undefined, {
    message: "Nothing to update",
});
export const TaskDependencyCreateSchema = z.object({
    depends_on_id: z.number().int().positive(),
});
export const TaskUpdateCreateSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
    mentions: z.array(z.number().int().positive()).max(50).optional(),
    /** Schritt 2: intern als gast-sichtbar posten (in den Gast-Thread). */
    guest_visible: z.boolean().optional(),
});
export const TaskNoteCreateSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
});
// Bearbeiten: gleiche Form wie beim Anlegen (nur der Body ändert sich).
export const TaskNoteUpdateSchema = TaskNoteCreateSchema;
// ---------- notifications ----------
export const NOTIFICATION_TYPES = [
    "mention",
    "assignment",
    "update",
    "status_change",
    "project_share",
    // "reminder" und "follow_up" schreibt der Server seit jeher in
    // notifications.type (reminderScheduler / followUpScheduler), die Liste hier
    // hinkte nur hinterher — der Cast in routes/notifications.ts war dadurch
    // unwahr. Mit dem Push-Kanal (0090) braucht jeder Typ einen Toggle, also ist
    // die Liste jetzt vollstaendig.
    "reminder",
    "follow_up",
];
export const SUPPORTED_LANGUAGES = ["de", "en"];
/**
 * Oberflächen-Design pro Benutzer (Migration 0095), gilt für Web UND iOS.
 * 'classic' = bisheriges Design (Standard, auch ohne user_preferences-Zeile),
 * 'juunit' = neues Design. Der CHECK in 0095 spiegelt diese Liste.
 */
export const UI_DESIGNS = ["classic", "juunit"];
/**
 * UX-Vorschläge 2026-10: ein Server-Flag pro Vorschlag, drei Stufen.
 *   off      = für niemanden
 *   vorschau = nur für Benutzer mit ui_design = "juunit" (Hauptschalter „Neues Design“)
 *   an       = für alle, auch "classic"
 * Standard im Code: "vorschau". Override pro Flag per Env `UX_FLAG_<NAME>`
 * (z. B. UX_FLAG_UX_WEB_P3=off). GET /api/me liefert die aufgelösten Stufen
 * als `ux_flags`; die Clients kombinieren sie selbst mit ui_design:
 *   aktiv = Stufe "an" ODER (Stufe "vorschau" UND ui_design === "juunit").
 */
export const UX_FLAG_NAMES = [
    "ux_web_p1",
    "ux_web_p2",
    "ux_web_p3",
    "ux_web_p4",
    "ux_web_p5",
    "ux_web_p6",
    "ux_web_p7",
    "ux_ios_p1",
    "ux_ios_p2",
    "ux_ios_p3",
    "ux_ios_p4",
    "ux_ios_p5",
    "ux_ios_p6",
];
export const UX_FLAG_MODES = ["off", "vorschau", "an"];
export const UX_FLAG_DEFAULT_MODE = "vorschau";
/**
 * Stufe eines Flags aus dem `ux_flags`-Feld von GET /api/me (Entscheidung Leiter
 * 27.10.2026, gilt für Web UND iOS):
 *   (a) Feld `ux_flags` fehlt ganz (alter Server, kennt die neuen Routen nicht) → "off"
 *   (b) Feld da, einzelner Schlüssel fehlt                                    → "vorschau"
 *   (c) Schlüssel da, Wert unbekannt                                          → "off"
 */
export function resolveUxFlagMode(uxFlags, name) {
    if (uxFlags == null || typeof uxFlags !== "object" || Array.isArray(uxFlags))
        return "off";
    const raw = uxFlags[name];
    if (raw === undefined)
        return UX_FLAG_DEFAULT_MODE;
    return UX_FLAG_MODES.includes(raw) ? raw : "off";
}
/** Gemeinsame Auswertung für Server und Web (iOS spiegelt sie in UXFlags). Unbekannt/fehlend = aus. */
export function uxFlagActive(mode, uiDesign) {
    return mode === "an" || (mode === "vorschau" && uiDesign === "juunit");
}
/** Bequemlichkeit: direkt aus dem /api/me-Feld + ui_design. */
export function uxFlagEnabledFromMe(uxFlags, name, uiDesign) {
    return uxFlagActive(resolveUxFlagMode(uxFlags, name), uiDesign);
}
/**
 * Spalten-Defaults von user_preferences als eine Quelle.
 *
 * Gilt fuer jeden Nutzer, der noch keine user_preferences-Zeile hat, und wird
 * von Server (GET /api/me/preferences) UND Web-Client (optimistischer
 * Anfangszustand) benutzt. Vorher stand dasselbe Objekt an vier Stellen
 * abgeschrieben — beim Hinzufuegen der Push-Felder (0090) waeren das vier
 * Stellen gewesen, an denen man eine haette vergessen koennen.
 *
 * MUSS mit den DEFAULT-Werten in der Migration uebereinstimmen.
 */
export const DEFAULT_USER_PREFERENCES = {
    channel_email: true,
    channel_teams: false,
    notify_mentions: true,
    notify_assignments: true,
    notify_self_pings: true,
    visible_departments: [],
    dashboard_layout: [],
    language: "de",
    default_list_id: null,
    channel_push: true,
    channel_push_mac: true,
    push_mentions: true,
    push_assignments: true,
    push_updates: true,
    push_status_changes: true,
    push_project_shares: true,
    push_reminders: true,
    push_follow_ups: true,
    push_quiet_hours: false,
    // 22:00 bzw. 07:00 als Minuten seit Mitternacht.
    push_quiet_start: 1320,
    push_quiet_end: 420,
    push_quiet_weekends: false,
    ui_design: "classic",
};
/** Push-Toggle-Feldname je NotificationType — eine Quelle für Server + Clients. */
export const PUSH_PREF_BY_TYPE = {
    mention: "push_mentions",
    assignment: "push_assignments",
    update: "push_updates",
    status_change: "push_status_changes",
    project_share: "push_project_shares",
    reminder: "push_reminders",
    follow_up: "push_follow_ups",
};
export const UserPreferencesUpdateSchema = z.object({
    channel_email: z.boolean().optional(),
    channel_teams: z.boolean().optional(),
    notify_mentions: z.boolean().optional(),
    notify_assignments: z.boolean().optional(),
    notify_self_pings: z.boolean().optional(),
    visible_departments: z.array(z.number().int().positive()).optional(),
    dashboard_layout: z
        .array(z.object({
        id: z.string().min(1).max(50),
        size: z.enum(["small", "half", "wide", "full"]),
        order: z.number().int(),
    }))
        .optional(),
    language: z.enum(["de", "en"]).optional(),
    /** null = Standard-Liste entfernen. Der Server prüft Existenz + Schreibrecht. */
    default_list_id: z.number().int().positive().nullable().optional(),
    // ---- Push (Migration 0090) ----
    channel_push: z.boolean().optional(),
    channel_push_mac: z.boolean().optional(),
    push_mentions: z.boolean().optional(),
    push_assignments: z.boolean().optional(),
    push_updates: z.boolean().optional(),
    push_status_changes: z.boolean().optional(),
    push_project_shares: z.boolean().optional(),
    push_reminders: z.boolean().optional(),
    push_follow_ups: z.boolean().optional(),
    push_quiet_hours: z.boolean().optional(),
    /** Minuten seit Mitternacht — der CHECK in 0090 spiegelt diese Grenzen. */
    push_quiet_start: z.number().int().min(0).max(1439).optional(),
    push_quiet_end: z.number().int().min(0).max(1439).optional(),
    push_quiet_weekends: z.boolean().optional(),
    // ---- Design-Schalter (Migration 0095) ----
    ui_design: z.enum(UI_DESIGNS).optional(),
});
// ---------- push devices (APNs, Migration 0090) ----------
/**
 * Registrierung eines iOS-Geraets fuer Push.
 *
 * `device_token` ist der Hex-String, den APNs der App aushaendigt (heute 64
 * Zeichen; Apple hat sich die Laenge ausdruecklich offengelassen, deshalb eine
 * Spanne statt einer festen 64).
 *
 * `environment` MUSS mitkommen: Debug-Builds bekommen Sandbox-Tokens,
 * TestFlight/App-Store-Builds Produktions-Tokens, und ein Token beim falschen
 * APNs-Host quittiert Apple mit BadDeviceToken. Das ist eine Eigenschaft des
 * Geraets, nicht des Servers — deshalb pro Zeile gespeichert.
 */
export const PushDeviceRegisterSchema = z.object({
    device_token: z.string().regex(/^[0-9a-fA-F]{64,200}$/, "expected a hex APNs device token"),
    environment: z.enum(["sandbox", "production"]),
    /** apns-topic. Fehlt er, nimmt der Server APNS_BUNDLE_ID. */
    bundle_id: z.string().min(1).max(200).optional(),
    /** Anzeigename in den Einstellungen ("iPhone von Markus"). */
    device_name: z.string().max(100).optional(),
    app_version: z.string().max(50).optional(),
    os_version: z.string().max(50).optional(),
});
// ---------- live activities (ActivityKit-Push, Migration 0091) ----------
/**
 * Registrierung einer laufenden Timer-Live-Activity.
 *
 * Das ist NICHT das Device-Token aus `push_devices`. ActivityKit stellt je
 * AKTIVITAET ein eigenes Push-Token aus, es kommt asynchron nach dem Start und
 * kann sich waehrend der Laufzeit AENDERN (`Activity.pushTokenUpdates`). Beides
 * zusammen in eine Zeile zu legen waere falsch: ein Geraet hat ein Device-Token
 * und im Laufe eines Tages viele Aktivitaets-Token.
 *
 * `activity_id` ist der Schluessel, nicht das Token: rotiert das Token, ist es
 * dieselbe Aktivitaet und die Zeile wird ueberschrieben statt verdoppelt.
 *
 * `started_at` schickt der Client mit, weil die Kachel es anzeigt — der Server
 * ueberschreibt es aber mit dem `started_at` des tatsaechlich laufenden
 * Zeiteintrags, sofern er einen findet. Die Anzeige haengt damit an der
 * Server-Wahrheit, nicht an der Uhr des Geraets.
 */
export const LiveActivityRegisterSchema = z.object({
    /** `Activity.id` aus ActivityKit. */
    activity_id: z.string().min(1).max(200),
    /** Hex-Token aus `Activity.pushTokenUpdates` (Laenge von Apple offengelassen). */
    push_token: z.string().regex(/^[0-9a-fA-F]{64,200}$/, "expected a hex ActivityKit push token"),
    /** Aufgabe, deren Timer die Kachel anzeigt. */
    task_id: z.number().int().positive(),
    /** ISO-Zeitpunkt, ab dem die Kachel hochzaehlt. */
    started_at: z.iso.datetime(),
    environment: z.enum(["sandbox", "production"]),
    /** Basis-Topic. Fehlt es, nimmt der Server APNS_BUNDLE_ID. Der
     *  `.push-type.liveactivity`-Zusatz kommt serverseitig dran. */
    bundle_id: z.string().min(1).max(200).optional(),
});
export const ProjectTemplateCreateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).optional().nullable(),
    payload: z.object({
        phases: z.array(z.object({
            name: z.string().min(1).max(200),
            color: hexColor.optional().nullable(),
            tasks: z.array(z.object({
                title: z.string().min(1).max(200),
                description: z.string().max(10000).optional().nullable(),
                priority: z.enum(TASK_PRIORITIES).optional().nullable(),
                is_milestone: z.boolean().optional(),
                offset_days_start: z.number().int().optional().nullable(),
                offset_days_end: z.number().int().optional().nullable(),
                // One level of subtasks (tasks.parent_id). Capped so a single task
                // cannot balloon the materialization.
                subtasks: z.array(z.object({
                    title: z.string().min(1).max(200),
                    description: z.string().max(10000).optional().nullable(),
                    priority: z.enum(TASK_PRIORITIES).optional().nullable(),
                    is_milestone: z.boolean().optional(),
                })).max(20).optional(),
            })),
        })).min(1).max(20),
    }),
});
// ---------- AI suggest + from-structure (Story 7 / AI flow) ----------
export const SuggestProjectStructureSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).optional().nullable(),
});
export const ProjectFromStructureSchema = ProjectCreateSchema.extend({
    payload: ProjectTemplateCreateSchema.shape.payload,
});
// ---------- AI parse-project-plan (upload a Markdown plan → full structure) ----------
/** Upload payload: the raw Markdown of a project plan. Only this user-supplied
 *  text leaves the system at the gateway (same egress class as parse-task). */
export const ParseProjectPlanSchema = z.object({
    plan: z.string().trim().min(1).max(50_000),
});
// ---------- KI: Aufgabenliste importieren (#1297) ----------
//
// Ablauf in zwei Runden, beide PROPOSE-ONLY:
//   1. POST /api/ai/import-task-list ohne `answers` → Vorschlag + Rückfrage der KI
//      (`message` immer, `questions` 0..3).
//   2. Optional derselbe Aufruf mit `answers` → überarbeiteter Vorschlag, ohne
//      weitere Fragen.
// Angelegt wird erst nach ausdrücklicher Bestätigung im Client über
//   - POST /api/projects/from-structure            (mode "new_project")
//   - POST /api/projects/:id/import-structure      (mode "project")
//   - POST /api/personal-tasks/bulk                (mode "personal")
export const TASK_IMPORT_TARGETS = ["auto", "new_project", "project", "personal"];
export const TaskImportAnalyzeSchema = z
    .object({
    text: z.string().trim().min(1).max(20_000),
    target: z.enum(TASK_IMPORT_TARGETS),
    /** Heutiges Datum beim Nutzer (Bezug für „Freitag", „nächste Woche"). */
    today: isoDate.optional(),
    /** Pflicht bei target "project": das Projekt, das gefüllt werden soll. */
    project_id: z.number().int().positive().optional(),
    /** Antworten auf die Rückfragen der ersten Runde. */
    answers: z
        .array(z.object({
        question: z.string().trim().min(1).max(500),
        answer: z.string().trim().min(1).max(1000),
    }))
        .max(5)
        .optional(),
})
    .refine((d) => d.target !== "project" || d.project_id !== undefined, {
    message: "project_id is required for target 'project'",
    path: ["project_id"],
});
const TaskImportTaskSchema = ProjectTemplateCreateSchema.shape.payload.shape.phases.element.shape.tasks.element;
export const ProjectImportStructureSchema = z.object({
    /** Bezugstag für offset_days_* (Default: heute, UTC). */
    start_date: isoDate.optional(),
    phases: z
        .array(z.object({
        /** Bestehende Phase des Projekts; ohne Angabe wird `name` neu angelegt. */
        phase_id: z.number().int().positive().optional().nullable(),
        name: z.string().trim().min(1).max(200),
        color: hexColor.optional().nullable(),
        tasks: z.array(TaskImportTaskSchema).max(50),
    }))
        .min(1)
        .max(20),
});
export const PersonalTasksBulkSchema = z.object({
    items: z
        .array(z.object({
        title: z.string().trim().min(1).max(500),
        notes: z.string().max(10_000).optional().nullable(),
        due_date: isoDate.optional().nullable(),
        priority: z.enum(TASK_PRIORITIES).optional().nullable(),
        list_id: z.number().int().positive().optional().nullable(),
        steps: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
    }))
        .min(1)
        .max(100),
});
export const LabelCreateSchema = z.object({
    name: z.string().trim().min(1).max(60),
    color: hexColor.optional().nullable(),
});
export const LabelUpdateSchema = LabelCreateSchema.partial();
export const StatusCreateSchema = z.object({
    name: z.string().trim().min(1).max(100),
    category: z.enum(STATUS_CATEGORIES),
    color: hexColor.optional().nullable(),
    sortOrder: z.number().int().min(0).optional(),
    wip_limit: z.number().int().min(1).max(999).optional().nullable(),
});
export const StatusUpdateSchema = StatusCreateSchema.partial();
export const StatusReorderSchema = z.object({
    orderedIds: z.array(z.number().int().positive()),
});
export const ListStatusCreateSchema = z.object({
    name: z.string().trim().min(1).max(100),
    category: z.enum(STATUS_CATEGORIES),
    color: hexColor.optional().nullable(),
    sort_order: z.number().int().min(0).optional(),
});
export const ListStatusUpdateSchema = ListStatusCreateSchema.partial();
export const ListStatusReorderSchema = z.object({
    orderedIds: z.array(z.number().int().positive()),
});
// ---------- custom field definitions (D4) ----------
export const CUSTOM_FIELD_TYPES = [
    "text",
    "number",
    "date",
    "select",
    "multiselect",
    "checkbox",
    "user",
    "url",
];
export const CustomFieldDefCreateSchema = z.object({
    key: z.string().trim().min(1).max(60).regex(/^[a-z0-9_]+$/, "Key must be lowercase alphanumeric + underscore"),
    label: z.string().trim().min(1).max(100),
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: z
        .object({
        choices: z.array(z.string().trim().min(1).max(200)).max(200).optional(),
    })
        .passthrough()
        .optional()
        .nullable(),
    required: z.boolean().optional(),
    sort_order: z.number().int().min(0).optional(),
    /** E4: scope this field def to a specific work item type (null = all types). */
    work_item_type_id: z.number().int().positive().nullable().optional(),
});
export const CustomFieldDefUpdateSchema = CustomFieldDefCreateSchema.partial();
export const CustomFieldDefReorderSchema = z.object({
    orderedIds: z.array(z.number().int().positive()),
});
// ---------- cycles (F3) ----------
export const CYCLE_STATUSES = ["upcoming", "active", "completed"];
// ---------- personal tasks / reminders / my-day (P1) ----------
export const REMINDER_CHANNELS = ["in_app", "teams", "email"];
export const REMINDER_STATUSES = ["pending", "sent", "cancelled"];
export const RECURRENCE_RULES = ["daily", "weekly", "monthly"];
// ---------- InfoCenter ("Was ist neu") ----------
export const INFO_ENTRY_TYPES = [
    "feature",
    "bugfix",
    "improvement",
    "announcement",
];
export const INFO_ENTRY_SOURCES = ["manual", "ai"];
export const INFO_GENERATION_STATUSES = [
    "idle",
    "running",
    "success",
    "error",
];
export const infoEntryCreateSchema = z.object({
    type: z.enum(INFO_ENTRY_TYPES),
    title: z.string().trim().min(3).max(200),
    description: z.string().trim().max(1000),
    module_key: z.string().trim().max(100).optional().nullable(),
    source_commits: z
        .array(z.object({
        sha: z.string().max(64),
        url: z.url().max(500),
    }))
        .optional()
        .nullable(),
    is_published: z.boolean().optional(),
});
export const infoEntryUpdateSchema = infoEntryCreateSchema.partial();
// ---------------------------------------------------------------------------
// Per-feature AI configuration (global default model/thinking + per-feature
// overrides). Every AI-backed feature can pick its own model and its own
// chain-of-thought ("thinking") mode; unset features inherit the global default.
// ---------------------------------------------------------------------------
/** Every AI-backed feature that can carry its own model + thinking override. */
export const AI_FEATURE_KEYS = [
    "summarize_task",
    "suggest_subtasks",
    "parse_task",
    "summarize_project",
    "suggest_project_structure",
    "parse_project_plan",
    "whats_new",
    "git_reconcile",
    "plan_my_day",
    "summarize_list",
    "suggest_steps",
    "summarize_personal_task",
    "draft_follow_up",
    "import_task_list",
];
/** German admin-UI labels for each feature key. */
export const AI_FEATURE_LABELS = {
    summarize_task: "Aufgabe zusammenfassen",
    suggest_subtasks: "Unteraufgaben vorschlagen",
    parse_task: "Freitext → Aufgabe",
    summarize_project: "Projekt zusammenfassen",
    suggest_project_structure: "Neues Projekt (Struktur)",
    parse_project_plan: "Plan importieren",
    whats_new: "Was ist neu (Changelog)",
    git_reconcile: "Git-Abgleich (juuOps-AI)",
    plan_my_day: "Tagesplanung (Mein Tag)",
    summarize_list: "Liste zusammenfassen",
    suggest_steps: "Schritte vorschlagen",
    summarize_personal_task: "Aufgabe zusammenfassen (Liste)",
    draft_follow_up: "Nachfrage-Mail vorschreiben",
    import_task_list: "Aufgabenliste importieren",
};
/** Resolvable thinking modes for the GLOBAL default. */
export const AI_THINKING_MODES = ["off", "on", "auto"];
/** Per-feature thinking: same as the default set plus "inherit" (use default). */
export const AI_FEATURE_THINKING_MODES = ["inherit", "off", "on", "auto"];
export const aiFeatureConfigSchema = z.object({
    feature_key: z.enum(AI_FEATURE_KEYS),
    provider_id: z.number().int().positive().nullable(),
    model: z.string().trim().min(1).max(200).nullable(),
    thinking: z.enum(AI_FEATURE_THINKING_MODES),
});
export const aiFeaturesUpdateSchema = z.object({
    thinking_default: z.enum(AI_THINKING_MODES),
    features: z.array(aiFeatureConfigSchema).max(AI_FEATURE_KEYS.length),
});
/** Runtime-selectable AI providers. 'bedrock' = Amazon Bedrock (region-derived endpoint, EU-only). */
export const AI_PROVIDERS = ["openai_compatible", "bedrock"];
/**
 * Create/update body for a provider. Discriminated on `provider`: bedrock carries
 * an AWS EU `region` (endpoint derived server-side, no base_url); openai_compatible
 * carries the SSRF-validated base_url. `api_key` is optional on update — omitting
 * it KEEPS the stored ciphertext.
 */
/** Admin-curated allowlist of model ids (deduped server-side; empty ⇒ no restriction). */
const allowedModelsSchema = z
    .array(z.string().trim().min(1).max(200))
    .max(200)
    .default([]);
export const aiProviderWriteSchema = z.discriminatedUnion("provider", [
    z.object({
        provider: z.literal("openai_compatible"),
        name: z.string().trim().min(1).max(100),
        base_url: z.string().min(1),
        model: z.string().trim().min(1).max(200),
        allowed_models: allowedModelsSchema,
        api_key: z.string().optional(),
        enabled: z.boolean(),
    }),
    z.object({
        provider: z.literal("bedrock"),
        name: z.string().trim().min(1).max(100),
        region: z.string().min(1),
        model: z.string().trim().min(1).max(200),
        allowed_models: allowedModelsSchema,
        api_key: z.string().optional(),
        enabled: z.boolean(),
    }),
]);
/** PUT /api/admin/ai-settings body — the global master switch + default provider. */
export const aiSettingsUpdateSchema = z.object({
    enabled: z.boolean(),
    default_provider_id: z.number().int().positive().nullable(),
});
// ---------------------------------------------------------------------------
// Email integration (admin-configurable O365 mail) — see routes/admin/emailSettings
// ---------------------------------------------------------------------------
/**
 * PUT /api/admin/email-settings body. `client_secret` is WRITE-ONLY: omit it to
 * keep the stored secret, send a non-empty string to replace it, or send "" /
 * null to clear it. It is never returned in any DTO.
 */
export const emailSettingsUpdateSchema = z.object({
    enabled: z.boolean(),
    tenant_id: z.string().trim().max(200).nullable().optional(),
    client_id: z.string().trim().max(200).nullable().optional(),
    client_secret: z.string().max(2000).nullable().optional(),
    system_sender: z.string().trim().email().max(320).nullable().optional(),
    send_on_behalf_enabled: z.boolean(),
});
// ---------------------------------------------------------------------------
// Follow-up requests (scheduled "Nachfrage an Kollegen") — see routes/followUps
// ---------------------------------------------------------------------------
export const FOLLOW_UP_RECURRENCES = ["none", "daily", "weekly"];
const isoDateTime = z
    .string()
    .refine((s) => !Number.isNaN(Date.parse(s)), { message: "invalid datetime" });
const isoDateOnly = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "recurrence_until must be YYYY-MM-DD");
/** POST /api/follow-ups body. */
export const followUpCreateSchema = z
    .object({
    recipient_user_id: z.number().int().positive(),
    subject: z.string().trim().min(1).max(200),
    message: z.string().trim().min(1).max(5000),
    remind_at: isoDateTime,
    task_id: z.number().int().positive().optional(),
    personal_task_id: z.number().int().positive().optional(),
    recurrence: z.enum(FOLLOW_UP_RECURRENCES).optional().default("none"),
    recurrence_until: isoDateOnly.nullable().optional(),
})
    .refine((d) => !(d.task_id !== undefined && d.personal_task_id !== undefined), {
    message: "At most one of task_id / personal_task_id may be set",
});
/** PATCH /api/follow-ups/:id body — edit while pending, or cancel. */
export const followUpPatchSchema = z.object({
    subject: z.string().trim().min(1).max(200).optional(),
    message: z.string().trim().min(1).max(5000).optional(),
    remind_at: isoDateTime.optional(),
    recurrence: z.enum(FOLLOW_UP_RECURRENCES).optional(),
    recurrence_until: isoDateOnly.nullable().optional(),
    status: z.literal("cancelled").optional(),
});
// ---------------------------------------------------------------------------
// Aufgaben zusammenfuehren (merge)
// ---------------------------------------------------------------------------
// Zwei Aufgaben werden zu EINER: alles, was an der Quelle haengt (Kommentare
// samt @-Erwaehnungen, Notizen, Anhaenge, Checkliste, Zeiten, Labels,
// Zustaendige, Beobachter, Abhaengigkeiten, Benachrichtigungen, Erinnerungen,
// Unteraufgaben, „Mein Tag"-Eintraege …) haengt danach am Ziel. Die Quelle
// bleibt als GRABSTEIN stehen: geschlossen, mit `merged_into_*` auf das Ziel —
// alte Deep-Links, Push-Tipps und Ticket-Nummern in Kommentaren landen so beim
// Ziel statt im Nichts. Genau eine der beiden Aufgaben ist danach offen.
//
// Nur GLEICHE Arten und (bei Projektaufgaben) dasselbe Projekt: Status, Phase,
// Labels, Custom Fields und Ticket-Nummern sind projektgebunden, eine
// Uebernahme ueber die Grenze waere verlustbehaftet.
/** POST /api/tasks/:id/merge — :id ist die QUELLE, sie wird geschlossen. */
export const TaskMergeSchema = z.object({
    target_task_id: z.number().int().positive(),
});
/** POST /api/personal-tasks/:id/merge — :id ist die QUELLE. */
export const PersonalTaskMergeSchema = z.object({
    target_personal_task_id: z.number().int().positive(),
});
/** POST /api/inbox/:source_type/:source_id/reply — Kommentar + Eintrag erledigt in einem Aufruf. */
export const InboxReplySchema = z.object({
    body: z.string().trim().min(1).max(10_000),
    /** Zusätzlich erwähnte Benutzer. */
    mentions: z.array(z.number().int().positive()).max(50).optional(),
    /**
     * Bei Erwähnungen den Verfasser des ursprünglichen Kommentars erwähnen,
     * damit er die Antwort mitbekommt. Standard true.
     */
    mention_author: z.boolean().optional(),
});
/**
 * PUT /api/my-day/order — Reihenfolge der Einträge eines Tages (UX Web P1, iOS P5).
 * entry_ids = my_day_entries.id in gewünschter Reihenfolge; alle müssen dem
 * Aufrufer und `day` gehören (sonst 404, nichts geändert). Nicht genannte
 * Einträge des Tages behalten ihren Platz-Wert. Antwort 204.
 */
export const MyDayOrderSchema = z.object({
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    entry_ids: z
        .array(z.number().int().positive())
        .min(1)
        .max(500)
        .refine((ids) => new Set(ids).size === ids.length, "entry_ids must be distinct"),
});
